import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { getIsAnyChatDrawerOpen } from '../../components/utils';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { currentConversationSidAtom } from '../../session/state';
import {
  ProviderDrawerState,
  drawerStateAtom,
  sessionConversationReadyAtom,
  sessionConversationSidAtom,
  totalUnreadMessageCountAtom,
  waitingVisitorsAtom,
} from '../state';
import { useOpenConversationCallback } from './useOpenConversationCallback';

export function useChatDrawerButtonUtils() {
  const [drawerState, setDrawerState] = useAtom(drawerStateAtom);
  const setCurrentConversationSid = useSetAtom(currentConversationSidAtom);

  const getSessionConversationSid = useAtomCallbackMemoized((get) => get(sessionConversationSidAtom), []);
  const getSessionConversationReady = useAtomCallbackMemoized((get) => get(sessionConversationReadyAtom), []);
  const getWaitingVisitors = useAtomCallbackMemoized((get) => get(waitingVisitorsAtom), []);
  const unreadMessageCount = useAtomValue(totalUnreadMessageCountAtom);
  const openConversation = useOpenConversationCallback();

  const toggleChatDrawer = (): void => {
    const sessionConversationSid = getSessionConversationSid();
    const sessionConversationReady = getSessionConversationReady();
    const waitingVisitors = getWaitingVisitors();
    const numWaitingVisitors = Object.values(waitingVisitors).length;
    const chatDrawerIsOpen = getIsAnyChatDrawerOpen(drawerState);
    //close drawer and reset current conversation if drawer is open
    if (chatDrawerIsOpen) {
      setDrawerState(null);
      setCurrentConversationSid(undefined);
      return;
    }
    // open session chat if there is a session conversation
    if (sessionConversationReady) {
      openConversation(ProviderDrawerState.SESSION_CHAT, sessionConversationSid);
    } else if (numWaitingVisitors > 1) {
      // open all clients chat if there are multiple waiting visitors but no session conversation
      setDrawerState(ProviderDrawerState.ALL_CLIENTS_CHAT_LIST);
    } else if (numWaitingVisitors === 1) {
      const { waitingRoomConversationSid } = Object.values(waitingVisitors)[0];

      // open waiting room chat if there is one waiting visitor but no session conversation
      openConversation(ProviderDrawerState.WAITING_ROOM_CHAT, waitingRoomConversationSid);
    } else {
      setDrawerState(ProviderDrawerState.WAITING_ROOM_CHAT);
    }
  };

  return {
    unreadMessageCount,
    toggleChatDrawer,
  };
}
